import { AppShellLoginState } from '@westwing/appshell_types';
import { useEffect } from 'react';
import { useShopToClubOverlayConfig } from './useShopToClubOverlayConfig';
import { oneDayTTL } from './useBellaCookies';
import useWasOverlayShown from './useWasOverlayShown';
import { OverlayShownLabel } from 'Interfaces/overlayTracking';
import { OverlayOpenOptions, PageType } from 'Types/types';
import { useHostProps } from 'Hooks/useHostProps';
import { useAppContext } from 'Components/SignupAndLoginMain/AppContext';
import { isAffiliateTraffic, isSemTraffic } from 'Helpers/traffic';
import { cookiesEnabled } from 'Helpers/environment';
import { useAppshellMeta, useFeatureToggle, useLoginState } from 'AppShell/hooks';

const lockerPageTypes = [
    PageType.HOMEPAGE,
    PageType.PDP,
    PageType.INTERIOR_DESIGN_SERVICE,
    PageType.LOOKS,
    PageType.CATEGORY,
    PageType.NEWPRODUCTS,
    PageType.ALLPRODUCTS,
    PageType.LANDING,
    PageType.BRANDS,
];

export const useShouldShowForcedShopToClub = (
    handleOverlayOpen: (openOptions: OverlayOpenOptions) => void,
    isOpen: boolean,
    keepForcedClose: boolean
): boolean => {
    const [hasBeenViewed, setHasBeenViewedStatus] = useWasOverlayShown();
    const loginState = useLoginState();
    const { isLoggedIn, isRegistered } = loginState as AppShellLoginState;
    const { isApp } = useAppshellMeta();

    const isActive = isOpen;

    const { expirationInDays } = useShopToClubOverlayConfig();

    const { p100ForceClubRegistrationPopup, p100ForceClubRegistrationPopupForAffiliate } = useFeatureToggle();
    const { pageType, isShopToClubEnabled } = useHostProps();
    const isDisabledFromBob = !isShopToClubEnabled;

    const {
        location: { search },
    } = useAppContext();
    const searchParams = new URLSearchParams(search);

    const isSemUrl = isSemTraffic(searchParams);
    const isAffiliateUrl = isAffiliateTraffic(searchParams);

    const canShowOnThisPage =
        lockerPageTypes.includes(pageType) &&
        ((isSemUrl && p100ForceClubRegistrationPopup) ||
            (isAffiliateUrl && p100ForceClubRegistrationPopupForAffiliate));

    const failConditions = [
        isRegistered,
        isApp,
        isLoggedIn,
        isDisabledFromBob,
        isActive,
        !canShowOnThisPage,
        keepForcedClose,
    ];
    const skipRender = failConditions.some(Boolean);

    useEffect(() => {
        const cookieTTL = expirationInDays * oneDayTTL;
        const wasExpirationDisabled = !expirationInDays && hasBeenViewed;
        if (wasExpirationDisabled && cookiesEnabled) {
            setHasBeenViewedStatus(0, cookieTTL);
        }

        if (!skipRender) {
            handleOverlayOpen({
                isLogin: false,
                isDismissible: false,
                isUndismissibleFromOutsideClick: true,
                trackingLabel: OverlayShownLabel.Default,
            });
        }
    }, [canShowOnThisPage, expirationInDays, handleOverlayOpen, hasBeenViewed, setHasBeenViewedStatus, skipRender]);

    return !skipRender;
};
